// src/components/Menu/MenuTop.js
import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { BASE_URL } from "../../Utils/api";
import {
  FiHome, FiFolder, FiSettings, FiChevronDown,
  FiUser, FiUsers, FiBriefcase, FiShoppingBag, FiTruck, FiFileText,
  FiPercent, FiFile, FiGrid, FiMail, FiCalendar, FiClock, FiBarChart2,
  FiMenu, FiX
} from "react-icons/fi";
import "./MenuTop.css";
import { GlobalContext } from "../../Utils/GlobalContext";
import { getPermFlags } from "../../Utils/controlaPermissao";
import UserSettingsMenu from "../UserSettings/UserSettingsMenu";
import NovidadesVersaoModal from "../Login/NovidadesVersaoModal";
import MenuLogoUploadModal from "./MenuLogoUploadModal";


const MenuTop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showNovidadesModal, setShowNovidadesModal] = useState(false);
  const [showLogoUploadModal, setShowLogoUploadModal] = useState(false);
  const navRef = useRef(null);

  const { globalData, setGlobalData } = useContext(GlobalContext);
  const {
    permiteTiposAgendamento,
    permiteDocumentos,
    permiteConfiguracoesCampos,
    permiteConfiguracoesEmail,
    constPermiteEmpresas,
    constPermiteFornecedores,
    constPermiteFuncionarios,
    constPermiteGerarAgendamentos,
    constPermiteGruposUsuarios,
    constPermiteIndicesCorrecao,
    constPermiteTiposDocumento,
    constPermiteTiposFornecedor,
    constPermiteTiposdePagamento,
    constPermiteUsuarios,
    constPermiteCamposCustomizados,
  } = getPermFlags(globalData);

  const temNovidades = Boolean(
    globalData?.temNovidades &&
      Array.isArray(globalData?.novidadesVersao) &&
      globalData.novidadesVersao.length > 0
  );
  const novidadesVersao = globalData?.novidadesVersao || [];
  const idEntidade = globalData?.entidade_id || localStorage.getItem("entidade_id");

  useEffect(() => {
    if (temNovidades) setShowNovidadesModal(true);
  }, [temNovidades]);

  // fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // fecha dropdown ao mudar de rota
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
    document.body.classList.remove("login-page-body");
  }, [location.pathname]);

  const handleNavigation = useCallback((route, classification) => {
    if (classification) {
      setGlobalData((prev) => ({ ...prev, classificacao: classification }));
    }
    setOpenMenu(null);
    setMobileOpen(false);
    navigate(route);
  }, [navigate, setGlobalData]);

  const handleLogoClick = () => {
    if (globalData?.logotipo) {
      handleNavigation("/PaginaInicial");
      return;
    }
    setShowLogoUploadModal(true);
  };

  const handleLogoSaved = (logotipo) => {
    setGlobalData((prev) => ({ ...prev, logotipo }));
  };

  const toggleMenu = (id) => setOpenMenu((prev) => (prev === id ? null : id));
  const toggleMobileNav = () => {
    setMobileOpen((prev) => !prev);
    setOpenMenu(null);
  };

  const handleCloseNovidadesModal = async () => {
    const ids = (novidadesVersao || []).map((n) => n.id).filter((id) => id != null);
    if (ids.length > 0) {
      try {
        const token = localStorage.getItem("authToken");
        await api.post(
          `${BASE_URL}/api/versoes/visualizar`,
          { id_versoes: ids },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error("Erro ao marcar novidades como visualizadas:", err);
      }
    }
    setGlobalData((prev) => ({ ...prev, temNovidades: false, novidadesVersao: [] }));
    setShowNovidadesModal(false);
  };

  const isActive = (path, classification) => {
    if (path === "Empresas" && classification) {
      return (
        location.pathname === `/${path}` &&
        globalData?.classificacao === classification
      );
    }
    return location.pathname === `/${path}`;
  };

  const activeGroup = () => {
    const path = location.pathname.substring(1);
    if (path === "Estatisticas" || path === "PainelConsolidadoDocumentos") return "painelGestao";
    if (
      path === "Empresas" || path === "Usuario" || path === "TiposFornecedor" ||
      path === "TiposDocumento" || path === "ClassificacaoTiposDocumento" ||
      path === "GrupoUsuario" || path === "IndicesCorrecao" || path === "TiposPagamento" ||
      path === "Documentos" || path === "Funcionarios"
    ) return "cadastros";
    if (
      path === "ConfiguracoesCampos" || path === "EmailNotificacao" ||
      path === "TiposAgendamento" || path === "Agendamentos" || path === "CamposCustomizados"
    ) return "configuracoes";
    return null;
  };

  const menuItems = [
    {
      id: "painelGestao",
      title: "Painel de Gestão",
      icon: <FiHome />,
      subMenus: [
        {
          title: "Estatísticas",
          icon: <FiBarChart2 />,
          path: "Estatisticas",
          onClick: () => handleNavigation("/Estatisticas"),
        },
        {
          title: "Painel Consolidado de Documentos",
          icon: <FiFile />,
          path: "PainelConsolidadoDocumentos",
          onClick: () => handleNavigation("/PainelConsolidadoDocumentos"),
        },
      ],
    },
    {
      id: "cadastros",
      title: "Cadastros",
      icon: <FiFolder />,
      subMenus: [
        permiteDocumentos && {
          title: "Documentos",
          icon: <FiFile />,
          path: "Documentos",
          onClick: () => handleNavigation("/Documentos"),
        },
        constPermiteEmpresas && {
          title: "Filiais",
          icon: <FiShoppingBag />,
          path: "Empresas",
          classification: "V",
          onClick: () => handleNavigation("/Empresas", "V"),
        },
        false && {
          title: "Entidades",
          icon: <FiBriefcase />,
          path: "Empresas",
          classification: "E",
          onClick: () => handleNavigation("/Empresas", "E"),
        },
        constPermiteFornecedores && {
          title: "Fornecedores",
          icon: <FiTruck />,
          path: "Empresas",
          classification: "F",
          onClick: () => handleNavigation("/Empresas", "F"),
        },
        constPermiteFuncionarios && {
          title: "Funcionários",
          icon: <FiUser />,
          path: "Funcionarios",
          onClick: () => handleNavigation("/Funcionarios"),
        },
        constPermiteIndicesCorrecao && {
          title: "Índices de Correção",
          icon: <FiPercent />,
          path: "IndicesCorrecao",
          onClick: () => handleNavigation("/IndicesCorrecao"),
        },
        constPermiteTiposDocumento && {
          title: "Tipos Documento",
          icon: <FiFileText />,
          path: "TiposDocumento",
          onClick: () => handleNavigation("/TiposDocumento"),
        },
        constPermiteTiposDocumento && {
          title: "Classificação Tipos de Documento",
          icon: <FiFileText />,
          path: "ClassificacaoTiposDocumento",
          onClick: () => handleNavigation("/ClassificacaoTiposDocumento"),
        },
        constPermiteTiposFornecedor && {
          title: "Tipos de Fornecedor",
          icon: <FiFileText />,
          path: "TiposFornecedor",
          onClick: () => handleNavigation("/TiposFornecedor"),
        },
        constPermiteTiposdePagamento && {
          title: "Tipos de Pagamento",
          icon: <FiFileText />,
          path: "TiposPagamento",
          onClick: () => handleNavigation("/TiposPagamento"),
        },
      ]
        .filter(Boolean)
        .sort((a, b) => a.title.localeCompare(b.title)),
    },
    {
      id: "configuracoes",
      title: "Configurações",
      icon: <FiSettings />,
      subMenus: [
        permiteConfiguracoesCampos && {
          title: "Configurações de Campo",
          icon: <FiGrid />,
          path: "ConfiguracoesCampos",
          onClick: () => handleNavigation("/ConfiguracoesCampos"),
        },
        permiteConfiguracoesEmail && {
          title: "Email para Notificações",
          icon: <FiMail />,
          path: "EmailNotificacao",
          onClick: () => handleNavigation("/EmailNotificacao"),
        },
        constPermiteGerarAgendamentos && {
          title: "Agendamentos",
          icon: <FiClock />,
          path: "Agendamentos",
          onClick: () => handleNavigation("/Agendamentos"),
        },
        constPermiteGruposUsuarios && {
          title: "Grupos de Usuários",
          icon: <FiUsers />,
          path: "GrupoUsuario",
          onClick: () => handleNavigation("/GrupoUsuario"),
        },
        permiteTiposAgendamento && {
          title: "Tipos de Agendamento",
          icon: <FiCalendar />,
          path: "TiposAgendamento",
          onClick: () => handleNavigation("/TiposAgendamento"),
        },
        constPermiteUsuarios && {
          title: "Usuários",
          icon: <FiUser />,
          path: "Usuario",
          onClick: () => handleNavigation("/Usuario"),
        },
        constPermiteCamposCustomizados && {
          title: "Campos Customizados",
          icon: <FiGrid />,
          path: "CamposCustomizados",
          onClick: () => handleNavigation("/CamposCustomizados"),
        },
      ]
        .filter(Boolean)
        .sort((a, b) => a.title.localeCompare(b.title)),
    },
  ];

  const grupoAtivo = activeGroup();

  const logoValor = typeof globalData?.logotipo === "string" ? globalData.logotipo : "";
  const logoSrc = logoValor
    ? /^(data:|blob:|https?:\/\/|\/)/i.test(logoValor)
      ? logoValor
      : `data:image/png;base64,${logoValor}`
    : null;

  return (
    <>
      <nav className={`menu-top${mobileOpen ? " mobile-open" : ""}`} ref={navRef}>
        {/* Logo */}
        <div className="menu-top-logo" onClick={handleLogoClick}>
          {logoSrc ? (
            <img src={logoSrc} alt="Logo" className="menu-top-logo-img" />
          ) : (
            <span className="menu-top-logo-text">Enviar logo</span>
          )}
        </div>

        {/* Itens de navegação */}
        <button
          type="button"
          className="menu-top-mobile-toggle"
          onClick={toggleMobileNav}
          aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <FiX /> : <FiMenu />}
        </button>

        <div className="menu-top-nav">
          {menuItems.map((item) => (
            <div
              key={item.id}
              className={`menu-top-item${grupoAtivo === item.id ? " active" : ""}${openMenu === item.id ? " open" : ""}`}
            >
              <button
                className="menu-top-item-btn"
                onClick={() => toggleMenu(item.id)}
                aria-expanded={openMenu === item.id}
                aria-haspopup="true"
              >
                <span className="menu-top-item-icon">{item.icon}</span>
                <span className="menu-top-item-title">{item.title}</span>
                <span className={`menu-top-chevron${openMenu === item.id ? " rotated" : ""}`}>
                  <FiChevronDown />
                </span>
              </button>

              {openMenu === item.id && item.subMenus.length > 0 && (
                <div className="menu-top-dropdown" role="menu">
                  {item.subMenus.map((sub, idx) => (
                    <button
                      key={idx}
                      className={`menu-top-dropdown-item${isActive(sub.path, sub.classification) ? " active" : ""}`}
                      onClick={sub.onClick}
                      onMouseDown={(e) => {
                        if (e.button === 1) {
                          e.preventDefault();
                          window.open(`${window.location.origin}/${sub.path}`, "_blank");
                        }
                      }}
                      role="menuitem"
                    >
                      <span className="menu-top-dropdown-icon">{sub.icon}</span>
                      {sub.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Área do usuário */}
        <div className="menu-top-user">
          <UserSettingsMenu />
        </div>
      </nav>

      <NovidadesVersaoModal
        show={showNovidadesModal}
        novidadesVersao={novidadesVersao}
        onClose={handleCloseNovidadesModal}
      />
      <MenuLogoUploadModal
        show={showLogoUploadModal}
        onHide={() => setShowLogoUploadModal(false)}
        idEntidade={idEntidade}
        onSaved={handleLogoSaved}
      />
    </>
  );
};

export default MenuTop;
